import React, { useEffect, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { getAutoCalendar } from '../../utils/api';

const STATUS_DOT = {
  scheduled: 'bg-blue-400',
  published: 'bg-emerald-400',
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const ContentCalendar = ({ clientId, refreshKey }) => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [items, setItems] = useState([]);

  const fetchItems = useCallback(async () => {
    try {
      const res = await getAutoCalendar({ client_id: clientId, year, month });
      setItems(res.data.items);
    } catch (err) {
      toast.error('Failed to load calendar');
    }
  }, [clientId, year, month]);

  useEffect(() => { fetchItems(); }, [fetchItems, refreshKey]);

  const shiftMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m);
    setYear(y);
  };

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDay = new Date(year, month - 1, 1).getDay();

  const itemsByDay = {};
  items.forEach((it) => {
    const iso = it.status === 'published' ? it.published_time : it.scheduled_time;
    if (!iso) return;
    const d = new Date(iso);
    if (d.getFullYear() === year && d.getMonth() + 1 === month) {
      const day = d.getDate();
      itemsByDay[day] = itemsByDay[day] || [];
      itemsByDay[day].push(it);
    }
  });

  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  return (
    <div data-testid="content-calendar">
      <div className="flex items-center justify-between mb-4">
        <button data-testid="calendar-prev-btn" onClick={() => shiftMonth(-1)} className="p-2 rounded-lg border border-divider text-gray-light hover:text-white">
          <ChevronLeft size={18} />
        </button>
        <h3 className="text-white font-bold text-lg" data-testid="calendar-month-label">{MONTHS[month - 1]} {year}</h3>
        <button data-testid="calendar-next-btn" onClick={() => shiftMonth(1)} className="p-2 rounded-lg border border-divider text-gray-light hover:text-white">
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray uppercase font-semibold mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d}>{d}</div>)}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => (
          <div
            key={idx}
            className={`min-h-[90px] rounded-lg p-1.5 ${day ? 'bg-card-bg border border-divider' : ''} ${
              day === today.getDate() && month === today.getMonth() + 1 && year === today.getFullYear()
                ? 'border-lime/60'
                : ''
            }`}
          >
            {day && (
              <>
                <p className="text-xs text-gray mb-1">{day}</p>
                <div className="space-y-1">
                  {(itemsByDay[day] || []).slice(0, 3).map((it) => (
                    <div key={it.id} title={`${it.title} (${it.status})`} className="flex items-center gap-1 bg-black rounded px-1.5 py-1">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[it.status] || 'bg-gray-400'}`} />
                      <span className="text-[10px] text-gray-light truncate">{it.title || it.topic}</span>
                    </div>
                  ))}
                  {(itemsByDay[day] || []).length > 3 && (
                    <p className="text-[10px] text-gray">+{itemsByDay[day].length - 3} more</p>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-5 mt-4 text-xs text-gray">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-400" /> Scheduled</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Published</span>
      </div>
    </div>
  );
};
